// How a checked-in room stands against its check-out time. The stay runs on a 24-hour clock
// (a guest checks out the same hour they checked in, N days later), so the booked end
// date-time is the moment the room is due back.

export type CheckoutState = "overdue" | "soon" | "ok";

export interface CheckoutTiming {
  state: CheckoutState;
  /** "Overdue 3h 20m", "Due in 1h 40m". */
  text: string;
  /** Minutes until due; negative once overdue. */
  minutes: number;
}

/** How close to the end counts as "soon" — the desk should be ready for the guest. */
export const DUE_SOON_MINUTES = 180;

const parseUtc = (iso: string) =>
  Date.parse(/(?:[zZ]|[+-]\d\d:\d\d)$/.test(iso) ? iso : `${iso}Z`);

const span = (totalMinutes: number) => {
  const m = Math.max(0, Math.round(totalMinutes));
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const mm = m % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${mm}m`;
  return `${mm}m`;
};

const timingAt = (end: number, now: number): CheckoutTiming => {
  const minutes = (end - now) / 60000;
  if (minutes < 0) return { state: "overdue", text: `Overdue ${span(-minutes)}`, minutes };
  return {
    state: minutes <= DUE_SOON_MINUTES ? "soon" : "ok",
    text: `Due in ${span(minutes)}`,
    minutes,
  };
};

export const checkoutTiming = (endDateTime: string | null | undefined, now: number = Date.now()): CheckoutTiming | null => {
  if (!endDateTime) return null;
  const end = parseUtc(endDateTime);
  if (Number.isNaN(end)) return null;
  return timingAt(end, now);
};

interface StayTimes {
  startDateTime: string;
  endDateTime: string;
  checkedInDateTime?: string | null;
}

/** When a room is due out: N nights after the moment the guest actually checked in, at the same
 * hour (the 24-hour clock). OTA stays are stored midnight to midnight, so their booked end alone
 * would flag them overdue from the start of check-out day. Falls back to the booked end. Keep in
 * step with the API's CheckoutDue.DueAtUtc. */
export const roomDueAt = (room: StayTimes): number | null => {
  const end = parseUtc(room.endDateTime);
  if (Number.isNaN(end)) return null;
  if (!room.checkedInDateTime) return end;
  const start = parseUtc(room.startDateTime);
  const checkedIn = parseUtc(room.checkedInDateTime);
  if (Number.isNaN(start) || Number.isNaN(checkedIn)) return end;
  const nights = Math.max(1, Math.round((end - start) / 3_600_000 / 24));
  return checkedIn + nights * 24 * 3_600_000;
};

export const checkoutTimingForRoom = (room: StayTimes, now: number = Date.now()): CheckoutTiming | null => {
  const due = roomDueAt(room);
  return due === null ? null : timingAt(due, now);
};
